import type { JobFlight, Segment } from '../../../src/types.ts';

/**
 * Pure helpers for showing an itinerary in full: which segments make up the
 * outbound and the return, how long each leg takes door to door, and how long
 * each connection is. No React here so the logic is unit-testable.
 */

export interface Leg {
  /** 'out' or 'return'. */
  kind: 'out' | 'return';
  segments: Segment[];
  from: string;
  to: string;
  /** Time on the road from first departure to last arrival, in minutes; null when times are missing. */
  totalMinutes: number | null;
  /** Layover before each segment after the first (index 0 is always null). */
  layoverMinutes: Array<number | null>;
  stops: number;
  departs: string | null;
  arrives: string | null;
}

function parse(t: string | null | undefined): number | null {
  if (!t) return null;
  const ms = Date.parse(t);
  return Number.isFinite(ms) ? ms : null;
}

function minutesBetween(a: string | null | undefined, b: string | null | undefined): number | null {
  const x = parse(a);
  const y = parse(b);
  if (x == null || y == null) return null;
  return Math.round((y - x) / 60_000);
}

function buildLeg(kind: Leg['kind'], segments: Segment[]): Leg {
  const first = segments[0];
  const last = segments[segments.length - 1];
  return {
    kind,
    segments,
    from: first?.from ?? '',
    to: last?.to ?? '',
    totalMinutes: minutesBetween(first?.dep, last?.arr),
    layoverMinutes: segments.map((s, i) => (i === 0 ? null : minutesBetween(segments[i - 1]?.arr, s.dep))),
    stops: Math.max(0, segments.length - 1),
    departs: first?.dep ?? null,
    arrives: last?.arr ?? null,
  };
}

/**
 * Split a fare's segments into its legs. A round trip turns around at the
 * first segment that lands at the trip destination; the server records how
 * many segments that took (`outboundSegments`), and when it did not, we look
 * for the destination ourselves. One-way fares are a single leg.
 */
export function splitLegs(flight: Pick<JobFlight, 'segments' | 'destinationCode' | 'returnDate' | 'outboundSegments' | 'flightType'>): Leg[] {
  const segs = flight.segments ?? [];
  if (!segs.length) return [];
  const isReturn = Boolean(flight.returnDate) || /RETURN/i.test(flight.flightType ?? '');
  if (!isReturn) return [buildLeg('out', segs)];

  let split = flight.outboundSegments ?? 0;
  if (!(split > 0 && split < segs.length)) {
    const dest = (flight.destinationCode ?? '').toUpperCase();
    const idx = segs.findIndex((s) => (s.to ?? '').toUpperCase() === dest);
    split = idx >= 0 ? idx + 1 : Math.ceil(segs.length / 2);
  }
  if (split <= 0 || split >= segs.length) return [buildLeg('out', segs)];
  return [buildLeg('out', segs.slice(0, split)), buildLeg('return', segs.slice(split))];
}

/** "2h 50m"; "45m"; "1d 2h 05m" for the long hauls. */
export function fmtMinutes(min: number | null | undefined): string {
  if (min == null || !Number.isFinite(min) || min < 0) return '';
  const d = Math.floor(min / 1440);
  const h = Math.floor((min % 1440) / 60);
  const m = min % 60;
  if (d > 0) return `${d}d ${h}h ${String(m).padStart(2, '0')}m`;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m}m`;
}

/** Longest connection across all legs, for a "long layover" warning. */
export function longestLayover(legs: Leg[]): number {
  let max = 0;
  for (const leg of legs) for (const l of leg.layoverMinutes) if (l != null && l > max) max = l;
  return max;
}

/** Every airline that operates a segment, deduplicated, in order of appearance. */
export function airlinesOf(segments: Segment[]): Array<{ code: string | null; name: string; logo: string | null }> {
  const seen = new Map<string, { code: string | null; name: string; logo: string | null }>();
  for (const s of segments) {
    const key = s.airlineCode || s.airline || '?';
    if (!seen.has(key)) seen.set(key, { code: s.airlineCode ?? null, name: s.airline ?? key, logo: s.airlineLogo ?? null });
  }
  return [...seen.values()];
}

/** Segment-level line for copy/paste: "TK1140 MJI 19:50 → IST 23:40 (2h 50m)". */
export function itineraryText(flight: JobFlight): string {
  const lines: string[] = [];
  for (const leg of splitLegs(flight)) {
    lines.push(`${leg.kind === 'out' ? 'Outbound' : 'Return'} ${leg.from} → ${leg.to} · ${fmtMinutes(leg.totalMinutes)} · ${leg.stops === 0 ? 'direct' : `${leg.stops} stop${leg.stops > 1 ? 's' : ''}`}`);
    leg.segments.forEach((s, i) => {
      const lay = leg.layoverMinutes[i];
      if (lay != null) lines.push(`   layover ${fmtMinutes(lay)} in ${s.from ?? ''}`);
      lines.push(`   ${s.flightNo ?? ''} ${s.from ?? ''} ${shortTime(s.dep)} → ${s.to ?? ''} ${shortTime(s.arr)}${s.airline ? ` · ${s.airline}` : ''}`);
    });
  }
  lines.push(`Total ${flight.price} ${flight.currency} via ${flight.provider}`);
  return lines.join('\n');
}

function shortTime(t: string | null | undefined): string {
  if (!t) return '';
  const m = t.match(/(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  return m ? `${m[1]} ${m[2]}` : t;
}
